import React, { useEffect, useRef, useState } from "react";
import { AxiosResponse } from "axios";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { Product, ProductCategory, ProductType } from "../../models/Product";
import { ProcessingRequestListItem } from "../../models/ProcessingRequest";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { ProcessingRequestForm } from "../../forms/ProcessingRequestForm/ProcessingRequestForm";
import { getAllProducts } from "../../services/ProductService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getProcessingRequests } from "../../services/FinishingCenterService";
import { Utility } from "../../core/Utility";
import "./ProcessingRequestsPage.scss";

export const ProcessingRequestsPage: React.FC = () => {
  const {
    //load and set global filters
    state,
    selectedDateFromSet,
    selectedDateToSet,
  } = useApplicationContext();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const businessYear = state.businessYear ?? new Date().getFullYear();
  const yearStart = new Date(businessYear, 0, 1, 0, 0, 1);
  const yearEnd = new Date(businessYear, 11, 31, 23, 59, 59);
  const getDefaultRangeForBusinessYear = () => {
    const today = new Date();
    if (businessYear === today.getFullYear()) {
      // tekuća godina -> poslednjih 7 dana
      const from = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 7,
        0,
        0,
        1,
      );
      const to = today;
      return { from, to };
    }
    // druga poslovna godina -> 1.1. - 31.12. te godine
    const from = new Date(businessYear, 0, 1, 0, 0, 1);
    const to = new Date(businessYear, 11, 31, 23, 59, 59);
    return { from, to };
  };
  const defaultRange = getDefaultRangeForBusinessYear();

  const [dateFrom, setDateFrom] = useState<Date>(
    state.selectedDateFrom
      ? new Date(state.selectedDateFrom)
      : defaultRange.from,
  );
  const [dateTo, setDateTo] = useState<Date>(
    state.selectedDateTo ? new Date(state.selectedDateTo) : defaultRange.to,
  );

  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [productCategoriesApi, setProductCategoriesApi] = useState<
    ProductCategory[]
  >([]);
  const [selectedProductCategory, setSelectedProductCategory] = useState<
    ProductCategory | undefined
  >();
  const [selectedProduct, setSelectedProduct] = useState<Product>(
    {} as Product,
  );

  const [allRequests, setAllRequests] = useState<ProcessingRequestListItem[]>(
    [],
  );
  const [filteredRequests, setFilteredRequests] = useState<
    ProcessingRequestListItem[]
  >([]);

  const [processing, setProcessing] = useState<boolean>(false);
  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");
  const [refreshToggle, setRefreshToggle] = useState(false);
  const [selectedRequestForForm, setSelectedRequestForForm] = useState<
    ProcessingRequestListItem | undefined
  >(undefined);

  const [showFormKey, setShowFormKey] = useState<string>("");

  const [filters, setFilters] = useState<any>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    partnerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    productName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    originPlace: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
  });

  const toast = useRef<Toast | null>(null);

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "all",
  });

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshLookups = async () => {
    const [pResp, pcResp]: [AxiosResponse, AxiosResponse] = await Promise.all([
      getAllProducts(),
      getAllProductCategories(),
    ]);

    [pResp, pcResp].forEach(ensureSuccess);

    const pr = pResp?.data?.result ?? [];
    const pc = pcResp?.data?.result ?? [];

    setProductsApi(pr);
    setProductCategoriesApi(pc);

    return { pr, pc };
  };

  useEffect(() => {
    const run = async () => {
      try {
        setProcessing(true);
        const { pr, pc } = await refreshLookups();
        await loadRequests(dateFrom, dateTo, pr); // prosledi pr da nema race condition
      } catch (err) {
        console.error(err);
        toast.current?.show({
          severity: "error",
          detail: t("Greška pri učitavanju zahteva"),
          life: 3000,
        });
      } finally {
        setProcessing(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // kad se promene datum filteri – ponovo ucitaj sve sa backend-a
  useEffect(() => {
    if (!productsApi.length) return;
    setProcessing(true);
    loadRequests(dateFrom, dateTo)
      .catch((e) => console.error(e))
      .finally(() => setProcessing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, refreshToggle]);

  useEffect(() => {
    const { from, to } = getDefaultRangeForBusinessYear();
    setDateFrom(from);
    setDateTo(to);
    selectedDateFromSet(from);
    selectedDateToSet(to);
  }, [businessYear]);

  // kad se promeni kategorija ili proizvod – filtriraj lokalno
  useEffect(() => {
    applyLocalFilters(allRequests, productsApi);
    resetTableFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductCategory, selectedProduct, allRequests, productsApi]);

  const loadRequests = async (from: Date, to: Date, prArg?: Product[]) => {
    const response: AxiosResponse = await getProcessingRequests({
      dateFrom: from,
      dateTo: to,
    });

    const list: ProcessingRequestListItem[] = response.data?.result ?? [];
    setAllRequests(list);
    applyLocalFilters(list, prArg ?? productsApi);
  };

  const applyLocalFilters = (
    data: ProcessingRequestListItem[],
    pr: Product[],
  ) => {
    let result = [...data];

    result = result.filter((r) => {
      const prod = pr.find((p) => String(p.id) === String(r.productId));
      const categoryOk = selectedProductCategory
        ? prod?.category?.id === selectedProductCategory.id
        : true;
      const productOk = selectedProduct?.id
        ? String(r.productId) === String(selectedProduct.id)
        : true;

      return categoryOk && productOk;
    });

    result.sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return da < db ? 1 : -1;
    });

    setFilteredRequests(result);
  };

  const resetTableFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      partnerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      productName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      originPlace: { value: null, matchMode: FilterMatchMode.CONTAINS },
      status: { value: null, matchMode: FilterMatchMode.EQUALS },
    });
    setGlobalFilterValue("");
  };

  const onGlobalFilterChange = (e: any) => {
    const value = e.target.value;
    const _filters = { ...filters };
    _filters["global"].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  // promene datuma
  const onDateFromChange = (_: string, value: Date | null) => {
    if (!value) return;
    setDateFrom(value);
    selectedDateFromSet(value as Date);
  };

  const onDateToChange = (_: string, value: Date | null) => {
    if (!value) return;
    setDateTo(value);
    selectedDateToSet(value as Date);
  };

  // promene kategorije/proizvoda
  const handleProductCategoryChange = (_: string, value: any) => {
    const cat = productCategoriesApi.find(
      (item) => item.id === value,
    ) as ProductCategory;
    setSelectedProductCategory(cat);
    setSelectedProduct({} as Product);
  };

  const handleProductChange = (_: string, value: any) => {
    const prod = productsApi.find((item) => item.id === value) as Product;
    setSelectedProduct(prod);
  };

  // prikazi datum
  const formatDate = (row: ProcessingRequestListItem) => {
    if (!row.date) return "";
    return Utility.formatDate(new Date(row.date));
  };

  const formatProductName = (row: ProcessingRequestListItem) => {
    return row.productName ?? "";
  };

  const formatAmount = (row: ProcessingRequestListItem) => {
    if (!row.amountKg) return "";
    const value = Math.round(Number(row.amountKg));
    return `${value} kg`;
  };

  const formatQualityOk = (row: ProcessingRequestListItem) => {
    if (row.qualityOk === true) return "da";
    if (row.qualityOk === false) return "ne";
    return "";
  };

  const formatOptions = (row: ProcessingRequestListItem) => (
    <div className="action-buttons">
      <ButtonInput
        addStyle={{ border: "none", background: "#198754" }}
        labelText={""}
        icon="bi bi-plus-square"
        onButtonClick={() => {
          const id = row.id;
          navigate(`/processing-requests-details/${id}`);
        }}
      />
      <ButtonInput
        addStyle={{ border: "none", background: "#198754" }}
        labelText={""}
        icon="bi bi-pencil"
        onButtonClick={() => {
          // setujemo red koji editujemo
          setSelectedRequestForForm(row);
          // promena show key-ja trigeruje otvaranje modala u formi
          setShowFormKey(uuidv4());
        }}
      />
    </div>
  );

  const renderHeader = () => (
    <div className="search-container">
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          value={globalFilterValue}
          onChange={onGlobalFilterChange}
          placeholder={t("Globalna pretraga...") as string}
        />
      </span>
    </div>
  );

  return (
    <PageLayout>
      <Toast ref={toast} position="top-right" />

      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-truck header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            />
            <span className="header-left-title">
              {t("Lista zahteva za prijem naturalne robe")}
            </span>

            <span className="header-elements-text">{t("od")}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />

            <span className="header-elements">{t("do")}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />

            <span className="header-elements">{t("za")}</span>
            <DropdownInput
              id="productCategory"
              value={selectedProductCategory?.id}
              onValueChange={handleProductCategoryChange}
              optionValue="id"
              optionLabel="name"
              options={productCategoriesApi.filter(
                (oneCategory: ProductCategory) =>
                  oneCategory.type === ProductType.MercantileGoods,
              )}
              filter={false}
              labelText=""
              placeholderText={t("vrstu robe")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: false }}
              disabled={processing}
            />

            <span className="header-elements"> </span>
            <DropdownInput
              id="selectedProduct"
              value={selectedProduct?.id}
              onValueChange={handleProductChange}
              optionValue="id"
              optionLabel="name"
              options={productsApi.filter(
                (item) =>
                  item.category?.id === selectedProductCategory?.id &&
                  item.isNatural === true,
              )}
              filter={false}
              labelText=""
              placeholderText={t("Ime")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: false }}
              disabled={!selectedProductCategory}
            />

            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 0 0 1rem",
              }}
              labelText={"reset"}
              icon="bi bi-arrow-repeat"
              onButtonClick={() => {
                const { from, to } = getDefaultRangeForBusinessYear();
                setDateFrom(from);
                setDateTo(to);
                selectedDateFromSet(from);
                selectedDateToSet(to);
                setSelectedProductCategory(undefined);
                setSelectedProduct({} as Product);
              }}
              disabled={processing}
            />
          </div>

          <div className="header-right">
            <ProcessingRequestForm
              buttonLabel={t("Zahtev za prijem naturalne robe") as string}
              buttonIcon="bi bi-truck"
              buttonStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                marginRight: "1rem",
              }}
              refreshingData={() => setRefreshToggle((prev) => !prev)}
              request={selectedRequestForForm}
              show={showFormKey}
            />
          </div>
        </div>

        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              scrollable
              scrollHeight="80vh"
              sortMode="single"
              removableSort
              filters={filters}
              filterDisplay="row"
              globalFilterFields={[
                "partnerName",
                "productName",
                "originPlace",
                "address",
              ]}
              header={renderHeader()}
              emptyMessage={t("Nema zahteva za zadate kriterijume")}
              value={filteredRequests}
              showGridlines
              stripedRows
            >
              <Column
                field="number"
                header={t("Broj zahteva")}
                sortable
                style={{ minWidth: "120px" }}
              />
              <Column
                body={formatDate}
                header={t("Datum")}
                sortable
                style={{ minWidth: "100px" }}
              />
              <Column
                body={formatProductName}
                field="productName"
                header={t("Roba")}
                filter
                style={{ minWidth: "120px" }}
              />
              <Column
                field="partnerName"
                header={t("Dobavljač")}
                filter
                sortable
                style={{ minWidth: "140px" }}
              />
              <Column
                field="originPlace"
                header={t("Polazno mesto")}
                filter
                style={{ minWidth: "120px" }}
              />
              <Column
                body={formatAmount}
                header={t("Količina")}
                sortable
                style={{ minWidth: "100px" }}
              />
              <Column
                body={formatQualityOk}
                header={t("Kvalitet OK")}
                style={{ minWidth: "110px" }}
              />
              <Column
                body={formatOptions}
                header={t("Opcije")}
                style={{ minWidth: "120px" }}
              />
            </DataTable>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
